/**
 * Base64-encode a byte array in fixed-size chunks.
 *
 * `btoa(String.fromCharCode(...bytes))` spreads every byte as a function
 * argument, which overflows the call stack for large payloads (a ~600 KB
 * document upload threw "Maximum call stack size exceeded"). Converting in
 * 32 KB slices keeps the argument count safe while producing identical output.
 */
const bytesToBase64 = (bytes) => {
  let binary = "";
  const CHUNK = 0x8000; // 32 KB per slice
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

/**
 * Import RSA public key from Base64-encoded SPKI format
 */
export const importRsaPublicKey = async (base64PublicKey) => {
  const binaryString = atob(base64PublicKey);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return crypto.subtle.importKey(
    "spki",
    bytes.buffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
};

/**
 * Generate a random AES-256-GCM key
 */
export const generateAesKey = async () => {
  return crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true, // extractable for RSA wrapping
    ["encrypt", "decrypt"]  // Added "decrypt" for response decryption
  );
};

/**
 * Encrypt payload with AES-256-GCM
 * Returns Base64(IV[12] || ciphertext || authTag)
 */
export const encryptPayloadWithAes = async (payload, aesKey) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    aesKey,
    encoded
  );
  // Combine IV + ciphertext (which includes auth tag in WebCrypto)
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return bytesToBase64(combined);
};

/**
 * Encrypt AES key with RSA-OAEP public key
 * Returns Base64
 */
export const encryptAesKeyWithRsa = async (aesKey, rsaPublicKey) => {
  const rawKey = await crypto.subtle.exportKey("raw", aesKey);
  const encrypted = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    rsaPublicKey,
    rawKey
  );
  return bytesToBase64(new Uint8Array(encrypted));
};

/**
 * Generate a unique nonce
 */
export const generateNonce = () => crypto.randomUUID();

/**
 * Build the complete encrypted login payload
 * @param {string} username
 * @param {string} password
 * @param {object} systemInfo - { systemId, systemModel, systemName, os, loginType }
 * @param {function} fetchKeyFn - async function that returns { keyId, publicKey }
 * @returns {object} { keyId, encryptedPayload, encryptedAesKey }
 */
export const buildEncryptedLoginPayload = async (username, password, systemInfo, fetchKeyFn) => {
  // 1. Fetch RSA public key from server
  const { id, key: serverPublicKey } = await fetchKeyFn();

  // 2. Import RSA public key
  const rsaPublicKey = await importRsaPublicKey(serverPublicKey);

  // 3. Generate AES-256-GCM key
  const aesKey = await generateAesKey();

  // 4. Build payload with anti-replay fields
  const payload = {
    username,
    password,
    timestamp: Date.now(),
    nonce: generateNonce(),
    ...systemInfo,
  };

  // 5. Encrypt payload with AES-GCM
  const encryptedPayload = await encryptPayloadWithAes(payload, aesKey);

  // 6. Encrypt AES key with RSA-OAEP
  const encryptedAesKey = await encryptAesKeyWithRsa(aesKey, rsaPublicKey);

  return { id, payload: encryptedPayload, key: encryptedAesKey, aesKey };
};

/**
 * Decrypt server response encrypted with AES-256-GCM
 * Input format: Base64(IV[12] || ciphertext || authTag)
 * @param {string} encryptedBase64 - Base64-encoded encrypted response
 * @param {CryptoKey} aesKey - The same AES key used to encrypt the request
 * @returns {object} Parsed JSON response
 */
export const decryptResponseWithAes = async (encryptedBase64, aesKey) => {
  const binaryString = atob(encryptedBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Extract IV (first 12 bytes) and ciphertext+tag (rest)
  const iv = bytes.slice(0, 12);
  const ciphertextWithTag = bytes.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    aesKey,
    ciphertextWithTag
  );

  const jsonString = new TextDecoder().decode(decrypted);
  return JSON.parse(jsonString);
};
